import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Upload, Download, Trash2, FileText, Search, X, FolderOpen } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "sow", label: "SOW" },
  { key: "contract", label: "Contracts" },
  { key: "spec", label: "Specs" },
  { key: "pmp", label: "PM Docs" },
  { key: "other", label: "Other" },
];

const EXT_ICON = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase();
  const colors = { pdf: "text-rose-400", doc: "text-blue-400", docx: "text-blue-400", xls: "text-green-400", xlsx: "text-green-400", ppt: "text-orange-400", pptx: "text-orange-400" };
  return colors[ext] || "text-[#7d8590]";
};

function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "other", project_id: "" });
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [usage, setUsage] = useState(null);
  const fileRef = useRef();
  const loadReqRef = useRef(0);

  const load = useCallback(async () => {
    // Bumping this invalidates any still-in-flight load, so a slower
    // response to an earlier keystroke can't overwrite the results of a
    // more recent search/category change.
    const reqId = ++loadReqRef.current;
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.set("category", category);
      if (search) params.set("search", search);
      const [dRes, pRes, uRes] = await Promise.all([
        api.get(`/documents?${params}`),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/storage/usage").catch(() => ({ data: null })),
      ]);
      if (reqId !== loadReqRef.current) return;
      setDocs(dRes.data || []);
      setProjects(pRes.data || []);
      setUsage(uRes.data);
    } catch {
      if (reqId === loadReqRef.current) toast.error("Failed to load documents");
    }
  }, [category, search]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file) { toast.error("Select a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (form.name) fd.append("name", form.name);
      if (form.description) fd.append("description", form.description);
      fd.append("category", form.category);
      if (form.project_id) fd.append("project_id", form.project_id);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setShowModal(false);
      setFile(null);
      setForm({ name: "", description: "", category: "other", project_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); setForm(v => ({ ...v, name: v.name || f.name })); setShowModal(true); }
  };

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Documents</h1>
          <p className="text-sm text-[#7d8590] mt-1">SOWs, contracts, specs and project files</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Upload className="w-4 h-4 mr-1" /> Upload
        </Button>
      </div>

      {usage && (
        <div className="mb-6 bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-[#7d8590]">
              Storage used — <span className="text-white font-medium">{formatBytes(usage.used_bytes)}</span>
              {usage.limit_bytes != null && <> of {formatBytes(usage.limit_bytes)}</>}
            </span>
            {usage.over_limit && <span className="text-rose-400 font-medium">Over quota</span>}
          </div>
          {usage.limit_bytes != null && (
            <div className="h-1.5 rounded-full bg-[#1c2128] overflow-hidden">
              <div
                className={`h-full rounded-full ${usage.over_limit ? "bg-rose-400" : "bg-[#00F5D4]"}`}
                style={{ width: `${Math.min(100, (usage.used_bytes / usage.limit_bytes) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              className={`px-4 py-2 text-sm transition-colors ${category === c.key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#484f58]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search documents…"
            className="bg-[#0d1117] border border-[#21262d] text-white rounded-lg pl-9 pr-4 py-2 text-sm w-60 focus:outline-none focus:border-[#00F5D4]/50"
          />
        </div>
      </div>

      {/* Drop zone hint */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-4 mb-6 text-center transition-colors ${dragOver ? "border-[#00F5D4] bg-[#00F5D4]/5" : "border-[#21262d]"}`}
      >
        <p className="text-xs text-[#7d8590]">Drag & drop a file here to upload</p>
      </div>

      {/* Document grid */}
      {docs.length === 0 ? (
        <div className="text-center py-20">
          <FolderOpen className="w-10 h-10 text-[#484f58] mx-auto mb-3" />
          <p className="text-[#7d8590] text-sm">No documents yet. Upload your first file.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {docs.map(doc => (
            <div key={doc.doc_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4 hover:border-[#30363d] transition-colors group">
              <div className="flex items-start justify-between mb-3">
                <FileText className={`w-8 h-8 ${EXT_ICON(doc.original_filename)}`} />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleDownload(doc)} className="p-1 text-[#7d8590] hover:text-[#00F5D4]"><Download className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(doc)} className="p-1 text-[#7d8590] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-sm font-medium text-white truncate mb-1">{doc.name}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1c2128] text-[#7d8590] border border-[#30363d] capitalize">{doc.category}</span>
                <span className="text-[10px] text-[#484f58]">{formatBytes(doc.file_size)}</span>
              </div>
              {doc.description && <p className="text-xs text-[#7d8590] mt-2 line-clamp-2">{doc.description}</p>}
              <p className="text-[10px] text-[#484f58] mt-2">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ""}</p>
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Upload Document</h2>
              <button onClick={() => { setShowModal(false); setFile(null); }} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-[#30363d] rounded-lg p-8 text-center cursor-pointer hover:border-[#00F5D4]/50 transition-colors"
              >
                {file ? (
                  <div>
                    <FileText className="w-8 h-8 text-[#00F5D4] mx-auto mb-2" />
                    <p className="text-sm text-white">{file.name}</p>
                    <p className="text-xs text-[#7d8590]">{formatBytes(file.size)}</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-[#484f58] mx-auto mb-2" />
                    <p className="text-sm text-[#7d8590]">Click to choose a file</p>
                    <p className="text-xs text-[#484f58] mt-1">PDF, Word, Excel, PowerPoint, images — max 25 MB</p>
                  </div>
                )}
                <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { setFile(f); setForm(v => ({ ...v, name: v.name || f.name })); } }} />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Document name</label>
                <input value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} placeholder="Leave blank to use filename"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Description</label>
                <input value={form.description} onChange={e => setForm(v => ({ ...v, description: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Category</label>
                  <select value={form.category} onChange={e => setForm(v => ({ ...v, category: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {CATEGORIES.filter(c => c.key !== "all").map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Project (optional)</label>
                  <select value={form.project_id} onChange={e => setForm(v => ({ ...v, project_id: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    <option value="">None</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={handleUpload} disabled={uploading || !file} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {uploading ? "Uploading…" : "Upload"}
              </Button>
              <Button onClick={() => { setShowModal(false); setFile(null); }} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
